import { Routes } from '@angular/router';
import { RxStompConfig } from '@stomp/rx-stomp';
import { environment } from '../environments/environment';
import { authGuard, logoutGuard, notAuthGuard } from './core/auth';
import { Role } from './enum';
import { adGuard, roleGuard } from './guards';
import { RX_STOMP_CONFIG, RxStompService, RxStompServiceFactory } from './socket';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { LoginComponent } from './components/login/login.component';
import { LayoutComponent } from './components/layout/layout.component';
import { UserComponent } from './components/user/user.component';
import { InputFormDataComponent } from './components/input-form-data/input-form-data.component';
import { RanneyWellComponent } from './components/ranney-well/ranney-well.component';
import { CheckPointComponent } from './components/check-point/check-point.component';
import { MeasuresComponent } from './components/measures/measures.component';
import { UnitOfMeasuresComponent } from './components/unit-of-measures/unit-of-measures.component';
import { ValidationRulesComponent } from './components/validation-rules/validation-rules.component';
import { ReportsComponent } from './components/reports/reports.component';

export const routes: Routes = [
  {
    path: '',
    canActivateChild: [authGuard, roleGuard],
    component: LayoutComponent,
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
        data: { role: [Role.USER] },
      },
      {
        path: 'dashboard',
        title: 'Контролна табла',
        data: { role: [Role.USER] },
        loadComponent: () => import('./components/dashboard/dashboard.component').then(c => c.DashboardComponent),
      },
      {
        path: 'input-forms',
        component: InputFormDataComponent,
        data: { role: [Role.USER] },
        children: [
          {
            path: ':inputFormId',
            title: 'Мерења података',
            data: { role: [Role.USER] },
            loadComponent: () =>
              import('./components/input-form-data/list/input-form-data-list.component').then(
                c => c.InputFormDataListComponent
              ),
          },
          {
            path: ':inputFormId/edit/:inputFormDataId',
            title: 'Унос података',
            data: { role: [Role.USER] },
            loadComponent: () =>
              import('./components/input-form-data/edit/input-form-data-edit.component').then(
                c => c.InputFormDataEditComponent
              ),
          },
          {
            path: ':inputFormId/create',
            title: 'Креирање уноса података',
            data: { role: [Role.USER] },
            loadComponent: () =>
              import('./components/input-form-data/create/input-form-data-create.component').then(
                c => c.InputFormDataCreateComponent
              ),
          },
        ],
      },
      {
        path: 'users',
        component: UserComponent,
        data: { role: [Role.ADMIN, Role.SECURITY_ADMIN] },
        canActivateChild: [adGuard],
        children: [
          {
            path: '',
            title: 'Корисници',
            data: { role: [Role.ADMIN, Role.SECURITY_ADMIN] },
            loadComponent: () => import('./components/user/list/user-list.component').then(c => c.UserListComponent),
          },
          {
            path: 'create',
            title: 'Креирање корисника',
            data: { action: 'add', role: [Role.ADMIN, Role.SECURITY_ADMIN] },
            loadComponent: () => import('./components/user/edit/user-edit.component').then(c => c.UserEditComponent),
          },
          {
            path: ':id',
            title: 'Преглед корисника',
            data: { role: [Role.ADMIN, Role.SECURITY_ADMIN] },
            loadComponent: () =>
              import('./components/user/preview/user-preview.component').then(c => c.UserPreviewComponent),
          },
          {
            path: ':id/edit',
            title: 'Измена корисника',
            data: { action: 'edit', role: [Role.ADMIN, Role.SECURITY_ADMIN] },
            loadComponent: () => import('./components/user/edit/user-edit.component').then(c => c.UserEditComponent),
          },
        ],
      },
      {
        path: 'change-password',
        title: 'Промена лозинке',
        data: { role: [Role.USER] },
        canActivate: [adGuard],
        loadComponent: () =>
          import('./components/profile/change-password/change-password.component').then(c => c.ChangePasswordComponent),
      },
      {
        path: 'ranney-wells',
        component: RanneyWellComponent,
        data: { role: [Role.ADMIN, Role.CONTROLLER, Role.OPERATOR] },
        children: [
          {
            path: '',
            title: 'Рени бунари',
            data: { role: [Role.ADMIN, Role.CONTROLLER, Role.OPERATOR] },
            loadComponent: () =>
              import('./components/ranney-well/list/ranney-well-list.component').then(c => c.RanneyWellListComponent),
          },
          {
            path: ':id/edit',
            title: 'Измена рени бунара',
            data: { role: [Role.ADMIN, Role.CONTROLLER, Role.OPERATOR] },
            loadComponent: () =>
              import('./components/ranney-well/edit/ranney-well-edit.component').then(c => c.RanneyWellEditComponent),
          },
          {
            path: 'create',
            title: 'Креирање рени бунара',
            data: { action: 'add', role: [Role.ADMIN] },
            loadComponent: () =>
              import('./components/ranney-well/create/ranney-well-create.component').then(
                c => c.RanneyWellCreateComponent
              ),
          },
        ],
      },
      {
        path: 'check-point',
        component: CheckPointComponent,
        data: { role: [Role.ADMIN, Role.CONTROLLER, Role.OPERATOR] },
        children: [
          {
            path: '',
            title: 'Контролне тачке',
            data: { role: [Role.ADMIN, Role.CONTROLLER, Role.OPERATOR] },
            loadComponent: () =>
              import('./components/check-point/list/check-point-list.component').then(c => c.CheckPointListComponent),
          },
          {
            path: 'create',
            title: 'Креирање контролне тачаке',
            data: { action: 'add', role: [Role.ADMIN] },
            loadComponent: () =>
              import('./components/check-point/edit/check-point-edit.component').then(c => c.CheckPointEditComponent),
          },
          {
            path: ':id',
            title: 'Преглед контролне тачаке',
            data: { role: [Role.ADMIN, Role.CONTROLLER, Role.OPERATOR] },
            loadComponent: () =>
              import('./components/check-point/preview/check-point-preview.component').then(
                c => c.CheckPointPreviewComponent
              ),
          },
          {
            path: ':id/edit',
            title: 'Измена контролне тачаке',
            data: { action: 'edit', role: [Role.ADMIN] },
            loadComponent: () =>
              import('./components/check-point/edit/check-point-edit.component').then(c => c.CheckPointEditComponent),
          },
        ],
      },
      {
        path: 'measures',
        component: MeasuresComponent,
        data: { role: [Role.USER] },
        children: [
          {
            path: '',
            title: 'Мере',
            data: { role: [Role.USER] },
            loadComponent: () =>
              import('./components/measures/list/measure-list.component').then(c => c.MeasureListComponent),
          },
          {
            path: 'create',
            title: 'Креирање мере',
            data: { action: 'add', role: [Role.ADMIN] },
            loadComponent: () =>
              import('./components/measures/edit/measures-edit.component').then(c => c.MeasuresEditComponent),
          },
          {
            path: ':id',
            title: 'Преглед мера',
            data: { role: [Role.USER] },
            loadComponent: () =>
              import('./components/measures/preview/measures-preview.component').then(c => c.MeasuresPreviewComponent),
          },
          {
            path: ':id/edit',
            title: 'Измена  мере',
            data: { action: 'edit', role: [Role.ADMIN] },
            loadComponent: () =>
              import('./components/measures/edit/measures-edit.component').then(c => c.MeasuresEditComponent),
          },
        ],
      },
      {
        path: 'unit-of-measures',
        component: UnitOfMeasuresComponent,
        data: { role: [Role.USER] },
        children: [
          {
            path: '',
            title: 'Јединице мере',
            data: { role: [Role.USER] },
            loadComponent: () =>
              import('./components/unit-of-measures/list/unit-of-measures-list.component').then(
                c => c.UnitOfMeasuresListComponent
              ),
          },
          {
            path: 'create',
            title: 'Креирање јединице мере',
            data: { action: 'add', role: [Role.ADMIN] },
            loadComponent: () =>
              import('./components/unit-of-measures/edit/unit-of-measures-edit.component').then(
                c => c.UnitOfMeasuresEditComponent
              ),
          },
          {
            path: ':id',
            title: 'Преглед јединица мера',
            data: { role: [Role.USER] },
            loadComponent: () =>
              import('./components/unit-of-measures/preview/unit-of-measures-preview.component').then(
                c => c.UnitOfMeasuresPreviewComponent
              ),
          },
          {
            path: ':id/edit',
            title: 'Измена јединице мере',
            data: { action: 'edit', role: [Role.ADMIN] },
            loadComponent: () =>
              import('./components/unit-of-measures/edit/unit-of-measures-edit.component').then(
                c => c.UnitOfMeasuresEditComponent
              ),
          },
        ],
      },
      {
        path: 'validation-rules',
        component: ValidationRulesComponent,
        data: { role: [Role.USER] },
        children: [
          {
            path: '',
            title: 'Валидациона правила',
            data: { role: [Role.USER] },
            loadComponent: () =>
              import('./components/validation-rules/list/validation-rules-list.component').then(
                c => c.ValidationRulesListComponent
              ),
          },
          {
            path: 'create',
            title: 'Креирање валидационог правила',
            data: { action: 'add', role: [Role.ADMIN] },
            loadComponent: () =>
              import('./components/validation-rules/edit/validation-rules-edit.component').then(
                c => c.ValidationRulesEditComponent
              ),
          },
          {
            path: ':id',
            title: 'Преглед валидационих правила',
            data: { role: [Role.USER] },
            loadComponent: () =>
              import('./components/validation-rules/preview/validation-rules-preview.component').then(
                c => c.ValidationRulesPreviewComponent
              ),
          },
          {
            path: ':id/edit',
            title: 'Измена валидационог правила',
            data: { action: 'edit', role: [Role.ADMIN] },
            loadComponent: () =>
              import('./components/validation-rules/edit/validation-rules-edit.component').then(
                c => c.ValidationRulesEditComponent
              ),
          },
        ],
      },
      {
        path: 'reports',
        component: ReportsComponent,
        data: { role: [Role.USER] },
        children: [
          {
            path: '',
            redirectTo: 'out-of-range-measures',
            pathMatch: 'full',
            data: { role: [Role.USER] },
          },
          {
            path: 'out-of-range-measures',
            title: 'Мере ван опсега',
            data: { role: [Role.USER] },
            loadComponent: () =>
              import('./components/reports/out-of-range-measures/out-of-range-measures.component').then(
                c => c.OutOfRangeMeasuresComponent
              ),
          },
        ],
      },
      {
        path: 'logout',
        canActivate: [logoutGuard],
        data: { role: [Role.USER] },
        loadComponent: () => import('./components/logout/logout.component').then(c => c.LogoutComponent),
      },
    ],
    providers: [
      {
        provide: RX_STOMP_CONFIG,
        useFactory: () => {
          const config: RxStompConfig = {
            brokerURL: environment.webSocketUrl,
            heartbeatIncoming: 15000,
            heartbeatOutgoing: 15000,
            discardWebsocketOnCommFailure: true,
          };
          if (!environment.production) {
            config.debug = (msg: string): void => {
              console.log(new Date(), msg);
            };
          }
          return config;
        },
      },
      {
        provide: RxStompService,
        useFactory: RxStompServiceFactory,
        deps: [RX_STOMP_CONFIG],
      },
    ],
  },
  { path: 'login', canActivate: [notAuthGuard], component: LoginComponent },
  {
    path: 'activate-user/:activationKey',
    title: 'Активација налога',
    canActivate: [notAuthGuard],
    loadComponent: () =>
      import('./components/user/activate/activate-user.component').then(m => m.ActivateUserComponent),
  },
  {
    path: 'create-forgot-password-request',
    title: 'Креирање захтева зе измену шифре',
    canActivate: [notAuthGuard],
    loadComponent: () =>
      import('./components/user/create-forgot-password-request/create-forgot-password-request.component').then(
        m => m.CreateForgotPasswordRequestComponent
      ),
  },
  {
    path: 'reset-password/:resetKey',
    title: 'Измена шифре',
    canActivate: [notAuthGuard],
    loadComponent: () =>
      import('./components/user/reset-password/reset-password.component').then(m => m.ResetPasswordComponent),
  },
  { path: 'not-found', component: NotFoundComponent },
  { path: '**', redirectTo: '/not-found' },
];
